<script>
import FormsValidatedInput from "@/components/Forms/ValidatedInput.vue";
import ConditionalContext from "@/components/shared/ConditionalContext.vue";
import IconButton from "@/components/shared/IconButton.vue";
import { get, post } from "@/requests/server";

export default {
  name: "MailingListForm",
  components: { ConditionalContext, FormsValidatedInput, IconButton },
  props: {
    communityId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      provider: "brevo",
      apiKey: null,
      listId: null,
      tag: null,
      fetchingLists: false,
      lists: null,
      listsPage: 0,
      listsPerPage: 10,
      listsTotalCount: null,
      lastFetchParams: null,
      apiKeyHelpUrls: {
        brevo:
          "https://developers.brevo.com/docs/getting-started#using-your-api-key-to-authenticate",
        mailchimp: "https://mailchimp.com/help/about-api-keys/",
      },
      error: null,
      community: null,
    };
  },
  computed: {
    hasMoreLists() {
      return (
        this.listsTotalCount && this.listsTotalCount > (this.listsPage + 1) * this.listsPerPage
      );
    },
    listContactCount() {
      let lists = this.lists.filter((list) => list.id === this.listId);
      if (lists.length === 0) {
        return null;
      }
      return lists[0].contact_count ?? 0;
    },
    internalCommunityId() {
      if (this.community) {
        return this.community.id;
      }
      return this.communityId;
    },
  },
  watch: {
    provider(newVal, oldVal) {
      if (oldVal) {
        this.apiKey = null;
      }
    },
    apiKey() {
      this.lists = null;
      this.lastFetchParams = null;
      this.error = null;
    },
  },
  methods: {
    async prevPage() {
      if (this.listsPage > 0) {
        this.listsPage--;
      }
      await this.fetchLists();
    },
    async nextPage() {
      if (this.hasMoreLists) {
        this.listsPage++;
      }
      await this.fetchLists();
    },
    async fetchLists() {
      if (!this.apiKey || !this.provider) {
        return;
      }

      let params = {
        api_key: this.apiKey,
        provider: this.provider,
        page: this.listsPage,
        per_page: this.listsPerPage,
      };

      if (this.lastFetchParams === JSON.stringify(params)) {
        return;
      }

      this.fetchingLists = true;

      const { data } = await get(`mailinglists/lists`, {
        axiosRequestConfig: {
          params: params,
        },
        cleanupCallback: () => (this.fetchingLists = false),
        requestOptions: {
          cancelId: "mailinglists/lists",
        },
      });

      if (data.error) {
        this.error = data.error;
        return;
      }

      this.lists = data.lists;
      this.listsTotalCount = data.count;
      this.lastFetchParams = JSON.stringify(params);
    },
    async createIntegration() {
      if (!this.listId || !this.internalCommunityId) {
        return;
      }

      const { data } = await post(
        "/mailinglists",
        {
          provider: this.provider,
          api_key: this.apiKey,
          list_id: this.listId,
          community_id: this.internalCommunityId,
          community_editable: !!this.communityId,
          tag: this.tag,
        },
        {
          notifications: {
            action: this.$t(
              "components.admin.mailing_list_form.notifications.create_integration.action"
            ),
          },
        }
      );
      this.$emit("added", data);
    },
  },
};
</script>

<template>
  <validation-provider>
    <b-form>
      <h3>{{ $t("components.admin.mailing_list_form.new_sync_title") }}</h3>

      <forms-validated-input
        v-if="!communityId"
        :object-value="community"
        :value="community ? community.id : null"
        type="relation"
        name="community_id"
        :label="$t('components.admin.mailing_list_form.community_label')"
        :rules="{
          required: true,
        }"
        :query="{
          slug: 'communities',
          value: 'id',
          text: 'name',
          params: {
            for: 'admin',
          },
        }"
        @relation="community = $event"
      >
      </forms-validated-input>

      <b-form-group :label="$t('components.admin.mailing_list_form.provider_label')">
        <b-select v-model="provider">
          <b-select-option value="brevo">{{
            $t("components.admin.mailing_list_form.brevo_option")
          }}</b-select-option>
          <b-select-option value="mailchimp">{{
            $t("components.admin.mailing_list_form.mailchimp_option")
          }}</b-select-option>
        </b-select>
      </b-form-group>
      <b-form-group
        :label="$t('components.admin.mailing_list_form.api_key_label')"
        :validated="!!lists"
        :state="error ? false : lists ? true : null"
      >
        <b-input v-model="apiKey" type="text" :state="error ? false : lists ? true : null">
        </b-input>

        <template v-if="provider" #description>
          {{ $t("components.admin.mailing_list_form.get_key_description", { provider }) }}
          <a :href="apiKeyHelpUrls[provider]" target="_blank">{{
            $t("components.admin.mailing_list_form.instructions")
          }}</a
          >.
        </template>
      </b-form-group>

      <forms-validated-input
        v-if="provider === 'mailchimp'"
        v-model="tag"
        type="string"
        :label="$t('components.admin.mailing_list_form.tag.label')"
        name="tag"
        :description="$t('components.admin.mailing_list_form.tag.description')"
        :rules="{
          required: true,
        }"
      ></forms-validated-input>

      <b-form-group>
        <icon-button
          :onclick="fetchLists"
          :disabled="fetchingLists || !apiKey || !provider"
          icon="arrow-clockwise"
          variant="ghost-secondary"
        >
          {{ $t("components.admin.mailing_list_form.fetch_lists") }}
        </icon-button>
      </b-form-group>

      <b-alert v-if="error" variant="danger" show>
        <h4 class="alert-heading">
          <b-icon icon="exclamation-triangle-fill" />
          {{ $t("components.admin.mailing_list_form.fetch_lists_error_title") }}
        </h4>
        {{ error }}
      </b-alert>

      <b-form-group v-if="lists" :label="$t('components.admin.mailing_list_form.list.label')">
        <b-radio-group
          v-model="listId"
          class="list-selection"
          buttons
          button-variant="outline-secondary"
        >
          <b-radio v-for="list in lists" :key="list.id" :value="list.id"
            >{{ list.name }} <span class="list-id">(#{{ list.id }})</span></b-radio
          >
        </b-radio-group>
      </b-form-group>
      <div v-if="lists && listsTotalCount > listsPerPage" class="list-pagination">
        <icon-button
          :onclick="prevPage"
          :disabled="listsPage === 0 || fetchingLists"
          icon="arrow-left-circle"
          variant="ghost-secondary"
          size="sm"
          >{{ $t("components.admin.mailing_list_form.list.pagination.previous") }}</icon-button
        >
        <icon-button
          :onclick="nextPage"
          :disabled="!hasMoreLists || fetchingLists"
          icon="arrow-right-circle"
          variant="ghost-secondary"
          size="sm"
          >{{ $t("components.admin.mailing_list_form.list.pagination.next") }}</icon-button
        >
      </div>

      <conditional-context
        v-if="provider"
        background="white"
        :label="$t('components.admin.mailing_list_form.info.title')"
        show
        class="mx-0"
      >
        <template v-if="provider === 'brevo'">
          <p>{{ $t("components.admin.mailing_list_form.info.brevo.description") }}</p>
          <p>{{ $t("components.admin.mailing_list_form.info.brevo.cleanup") }}</p>
          <p>{{ $t("components.admin.mailing_list_form.info.brevo.manual") }}</p>
          <p v-if="listId && listContactCount > 0" class="alert alert-warning">
            <strong>{{ $t("components.admin.mailing_list_form.info.brevo.warning.title") }}</strong>
            <span>
              {{
                $t("components.admin.mailing_list_form.info.brevo.warning.message", {
                  count: listContactCount,
                })
              }}
            </span>
          </p>
        </template>
        <template v-if="provider === 'mailchimp'">
          <p>{{ $t("components.admin.mailing_list_form.info.mailchimp.description") }}</p>
          <p>{{ $t("components.admin.mailing_list_form.info.mailchimp.cleanup") }}</p>
          <p>{{ $t("components.admin.mailing_list_form.info.mailchimp.manual") }}</p>
        </template>
      </conditional-context>
      <icon-button
        :onclick="createIntegration"
        role="add"
        :disabled="!listId || !internalCommunityId"
        >{{ $t("forms.create") | capitalize }}</icon-button
      >
    </b-form>
  </validation-provider>
</template>

<style scoped lang="scss">
.list-pagination {
  display: flex;
  justify-content: space-between;
  margin-top: 0.25rem;
  margin-bottom: 1rem;
}
.list-selection {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  gap: 0.5rem;

  .list-id {
    color: $content-neutral-secondary;
    transition: all 0.15s ease-in-out;
  }

  .btn {
    border-radius: 0.25rem !important; // undo styling form button-group, which we can't remove

    &.active,
    &:hover {
      .list-id {
        color: $white;
      }
    }
  }
}
</style>
